import WebSocket from 'ws';
import type { LogFn } from '../log.js';
import type { ReconnectQuietSlot } from './reconnect-quiet-slot.js';
import type { ReconnectOutageTracker } from './reconnect-outage-plan.js';
import {
  armReconnectDelayTimer,
  applyTieredReconnectPlanAndLog,
  beginTieredSilentReconnectDisconnect,
  BRIDGE_DISCONNECT_LEAD_IN,
  BRIDGE_SERVICE_LABEL,
  clearTieredReconnectChannelOnOpen,
} from './tiered-channel-reconnect.js';
import type { DuplicateBridgeFlapTracker } from './duplicate-bridge-connection-detect.js';

export interface MainBridgeReconnectState {
  closedByUser: boolean;
  reconnectAttempt: number;
  reconnectTimeout: ReturnType<typeof setTimeout> | null;
  currentWs: WebSocket | null;
  mainQuiet: ReconnectQuietSlot;
  mainOutage: ReconnectOutageTracker;
  duplicateBridgeFlap: DuplicateBridgeFlapTracker;
  /** Last `close` while reconnecting; used when rescheduling after a synchronous connect error. */
  lastReconnectCloseMeta: { code: number; reason: string } | null;
  /** Next main bridge `open` should log reconnect after access token refresh (quiet-slot path may not). */
  logBridgeOpenAsReconnect: boolean;
}

/** Alias for combined bridge + closeable state typing. */
export type ReconnectState = MainBridgeReconnectState;

export function beginMainBridgeDeferredDisconnect(
  state: MainBridgeReconnectState,
  code: number,
  reason: string,
  log: LogFn,
  willReconnect: boolean,
): void {
  beginTieredSilentReconnectDisconnect({
    isClosedByUser: () => state.closedByUser,
    quiet: state.mainQuiet,
    code,
    reason,
    willReconnect,
    log,
    serviceLabel: BRIDGE_SERVICE_LABEL,
    shouldAbortQuietWindow: () => {
      const w = state.currentWs;
      return w != null && w.readyState === WebSocket.OPEN;
    },
  });
}

export function clearMainBridgeReconnectQuietOnOpen(state: MainBridgeReconnectState, log: LogFn): void {
  clearTieredReconnectChannelOnOpen({
    quiet: state.mainQuiet,
    outage: state.mainOutage,
    clearLastCloseMeta: () => {
      state.lastReconnectCloseMeta = null;
    },
    log,
    restoredMessage: 'Bridge connection restored.',
  });
}

export function scheduleMainBridgeReconnect(
  state: MainBridgeReconnectState,
  connect: () => void,
  log: LogFn,
  closeMeta?: { code: number; reason: string },
): void {
  if (state.closedByUser || state.currentWs != null) return;
  const meta = closeMeta ?? state.lastReconnectCloseMeta ?? undefined;
  const delay = applyTieredReconnectPlanAndLog(
    state.mainOutage,
    log,
    BRIDGE_SERVICE_LABEL,
    BRIDGE_DISCONNECT_LEAD_IN,
    meta?.code,
    meta?.reason,
  );
  armReconnectDelayTimer({
    delayMs: delay,
    bumpAttempt: () => {
      state.reconnectAttempt += 1;
    },
    clearTimer: () => {
      if (state.reconnectTimeout != null) {
        clearTimeout(state.reconnectTimeout);
        state.reconnectTimeout = null;
      }
    },
    setTimer: (id) => {
      state.reconnectTimeout = id;
    },
    shouldAbortBeforeRun: () => state.closedByUser || state.currentWs != null,
    run: connect,
    reschedule: () => {
      scheduleMainBridgeReconnect(state, connect, log);
    },
  });
}
